import { ErrorMessage } from 'anf-core-react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {
  useState, useEffect, useReducer, useRef,
} from 'react';
import useTranslatedText from '../../../hooks/useTranslatedText';
import Loader from '../../Loader';
import { ERROR_MESSAGE } from '../../Messages/Messages';
import UserPreferences from '../../UserPreferences';
import { navigationCategoryShape, footerShape } from '../propTypesShapes';
import MenuBack from './MenuBackButton';
import styles from './menu.module.scss';
import navStateReducer from './tools/navStateReducer';

/**
 * MenuDataWrapper component manages state for the mobile nav.
 * Renders a TopLevelMenu when viewing the L0 level of the nav, and a Menu component otherwise.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.initialCategoryData - the L0 category data
 * @param {Object} props.delayedCategoryData - the full category data, fetched after toaster opens
 * @param {Object} props.footerData - all data for the footer
 *
 * @returns {JSX.Element} The inside of the nav toaster.
 */
const MenuDataWrapper = ({
  initialCategoryData,
  delayedCategoryData,
  footerData,
  defaultCountry = '',
}) => {
  const allLabel = useTranslatedText('all', { fallback: 'All' });
  const [hasError, setHasError] = useState(false);
  const fullCategoryData = useRef(null);

  const [navState, dispatchNavState] = useReducer(
    navStateReducer,
    {
      currentLevel: -1,
      levels: [],
      menus: [],
    },
  );

  /* Gets a particular category's data out of the full dataset */
  const getCategoryFromFullData = (id) => {
    // If we're at the root of the data, return it as is
    if (initialCategoryData.id === id) {
      return initialCategoryData;
    }

    // Otherwise, use the category IDs stored in levels to drill down
    let currentData = fullCategoryData?.current?.navigationSubCategories;
    for (let i = 1; i < navState.levels.length; i += 1) {
      if (!currentData) {
        setHasError(true);
        return null;
      }
      if (currentData.find((cat) => cat.id === id)) {
        // when going backwards, we will find our target id before we finish the loop
        break;
      }
      const thisLevel = currentData.find((cat) => cat.id === navState.levels[i].id);
      currentData = thisLevel?.navigationSubCategories;
    }

    const targetLevel = currentData.find((cat) => cat.id === id);
    return targetLevel;
  };

  /* Gets the data for a category that's been clicked and dispatches a state update */
  const processCategoryClick = async (id, direction = 'forward') => {
    const nextLevelData = await getCategoryFromFullData(id);
    // If the data is incomplete, don't continue
    if (!nextLevelData) {
      setHasError(true);
      return;
    }
    dispatchNavState({
      type: direction,
      payload: {
        id: nextLevelData.id,
        label: nextLevelData.title,
        subCategories: nextLevelData.navigationSubCategories,
        espotIdentifier: nextLevelData.espotIdentifier,
        allLink: nextLevelData.mobileMenuUrl || nextLevelData.url,
      },
    });
  };

  /* Takes a category click and holds on to it if the data isn't ready yet */
  const categoryClickReducer = (state, action) => {
    switch (action.type) {
      case 'set':
        return { pendingClick: action.payload.click };
      case 'clear':
        if (state.pendingClick) {
          processCategoryClick(
            state.pendingClick.id,
            state.pendingClick.direction,
          );
        }
        return { pendingClick: null };
      default:
        return state;
    }
  };

  const [pendingCategoryClick, dispatchCategoryClick] = useReducer(
    categoryClickReducer,
    {
      pendingClick: null,
    },
  );

  /* Handles a click on a footer link, subcategory, or the back button */
  const handleClick = async (id, direction = 'forward', label) => {
    // reset errors and pending clicks
    setHasError(false);

    // make sure we don't have more than one menu
    // if it happened, trigger a reset
    if (navState.menus.length > 1) {
      dispatchNavState({
        type: 'reset',
      });
    }

    // check if this ID is part of the footer instead of a category
    const hasFooterData = footerData && footerData[id];

    // If this is in the footer, we'll get data from there, otherwise it's a category
    if (hasFooterData) {
      const footerLevelData = footerData[id];
      dispatchNavState({
        type: direction,
        payload: {
          id,
          label,
          subCategories: footerLevelData,
        },
      });
      return;
    }
    // If we haven't received our delayed data yet, we'll come back to this after
    if (!fullCategoryData.current) {
      dispatchCategoryClick({ type: 'set', payload: { click: { id, direction } } });
    } else {
      processCategoryClick(id, direction);
    }
  };

  useEffect(() => {
    // initial setup of menu with L0 data fetched on page load
    if (initialCategoryData) {
      dispatchNavState({
        type: 'forward',
        payload: {
          id: initialCategoryData.id,
          label: null,
          subCategories: initialCategoryData.navigationSubCategories,
          espotIdentifier: initialCategoryData.espotIdentifier,
        },
      });
    } else {
      setHasError(true);
    }
  }, [initialCategoryData]);

  useEffect(() => {
    if (delayedCategoryData) {
      fullCategoryData.current = delayedCategoryData;
      dispatchCategoryClick({ type: 'clear' });
    }
  }, [delayedCategoryData]);

  const setFocus = (event) => {
    const activeMenu = event.currentTarget;
    const firstFocusableElement = activeMenu.querySelectorAll('a, button')[0];
    if (firstFocusableElement) {
      firstFocusableElement.focus();
    }
    dispatchNavState({
      type: 'reset',
    });
  };

  return (
    <>
      {pendingCategoryClick.pendingClick ? (
        <div className={styles.loading}>
          <div className={styles.spinner}>
            <Loader />
          </div>
        </div>
      ) : null}
      {navState.currentLevel > 0 ? (
        <MenuBack
          handleClick={handleClick}
          id={navState.levels[navState.currentLevel - 1].id}
          label={navState.currentLevel === 1
            ? allLabel.value
            : navState.levels[navState.currentLevel - 1].label}
        />
      ) : (
        <div className={styles.back}>
          <UserPreferences defaultCountry={defaultCountry} parentComponent="small-screen-nav" />
        </div>
      )}
      {/* Show an error when there's a problem but avoid blowing away the current menu */}
      {hasError ? <ErrorMessage>{ERROR_MESSAGE}</ErrorMessage> : null}
      <div
        className={classNames({
          [styles[navState.direction]]: (navState.menus.length > 1),
        }, styles.menuContainer)}
      >
        {navState.menus.map((menu) => {
          const isActive = menu.id === navState.levels[navState.currentLevel]?.id;
          return (
            <div
              key={menu.id}
              className={styles.menu}
              data-testid={isActive ? 'category-nav-current' : undefined}
              onAnimationEnd={(event) => {
                if (isActive) {
                  setFocus(event);
                }
              }}
            >
              {React.createElement(menu.component.type, {
                ...menu.component.props,
                handleClick,
                footerData,
              })}
            </div>
          );
        })}
      </div>
    </>
  );
};

MenuDataWrapper.propTypes = {
  initialCategoryData: navigationCategoryShape,
  delayedCategoryData: navigationCategoryShape,
  footerData: footerShape,
  defaultCountry: PropTypes.string,
};

export default MenuDataWrapper;
